import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import * as ErrorReporting from 'services/error-reporting/error-reporting'
import { Component, ErrorInfo, ReactNode } from 'react'
import { analytics } from 'services/mixpanel/analytics-config'

interface Props {
  children: ReactNode
  showOopsDialog?: boolean
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.trackComponentError(error, errorInfo)
  }

  /**
   * Track React component errors
   */
  private trackComponentError(error: Error, errorInfo: ErrorInfo): void {
    ErrorReporting.captureException(error)
    try {
      analytics.track(AnalyticsEvents.Errors.ERROR_OCCURRED, {
        error_message: error.message,
        page: this.getCurrentPage(),
        stack_trace: this.formatErrorInfo(error, errorInfo),
      })
    } catch (trackingError) {
      console.error('Failed to track React error:', trackingError)
    }
  }

  /**
   * Format error information for analytics
   */
  private formatErrorInfo(error: Error, errorInfo: ErrorInfo): string {
    const errorStack = error.stack || 'No stack trace available'
    const componentStack = errorInfo.componentStack || 'No component stack available'

    return `Error Stack:\n${errorStack}\n\nComponent Stack:\n${componentStack}`
  }

  /**
   * Get current page/route for error context
   */
  private getCurrentPage(): string {
    try {
      return window.location.pathname + window.location.search
    } catch {
      return 'unknown'
    }
  }

  render() {
    if (this.state.hasError && this.props.showOopsDialog) {
      return (
        <div>
          <div className="flex min-h-screen items-center justify-center bg-background">
            <div className="max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-lg">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-foreground">
                  Oops! Something went wrong
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  We've encountered an unexpected error. Our team has been notified and is working
                  on a fix.
                </p>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-none"
                >
                  Reload Page
                </button>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                    Error Details (Development Only)
                  </summary>
                  <pre className="mt-2 overflow-auto rounded bg-muted p-2 text-xs text-muted-foreground">
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
